import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import publicMachinesApi from "./api/publicMachinesApi";
import publicCustomersApi from "./api/publicCustomersApi";
import { Machine } from "./models/machinesModels";
import { Customer } from "./models/customersModels";
import publicServiceReportsApi from "./api/publicServiceReportsApi";
import {
  CategoryReference,
  MachinePartGroup,
  ServiceReport,
  ServiceReportStatusCode,
  ServiceReportsQuery
} from "./models/serviceReportsModels";
import customerCategoriesApi from "../customer/shared/api/customerCategoriesApi";
import { Document } from "./models/documentsModels";
import publicDocumentsApi from "./api/publicDocumentsApi";

export const getMachine = createAsyncThunk(
  "public/getMachine",
  async (number: string) => {
    return publicMachinesApi.getMachine(number);
  }
);

export const getCustomer = createAsyncThunk(
  "public/getCustomer",
  async (id: string) => {
    return publicCustomersApi.getCustomer(id);
  }
);

export const getServiceReports = createAsyncThunk(
  "public/getServiceReports",
  async ({ machineNumber, query }: any) => {
    return publicServiceReportsApi.getServiceReports(machineNumber, query);
  }
);

export const getServiceReport = createAsyncThunk(
  "public/getServiceReport",
  async (serviceReportNumber: string) => {
    return publicServiceReportsApi.getServiceReport(serviceReportNumber);
  }
);

export const createServiceReport = createAsyncThunk(
  "public/createServiceReport",
  async ({ model, machineNumber }: any) => {
    return publicServiceReportsApi.createServiceReport(model, machineNumber);
  }
);

export const changeServiceReport = createAsyncThunk(
  "public/changeServiceReport",
  async ({ serviceReportNumber, model }: any) => {
    return publicServiceReportsApi.changeServiceReport(serviceReportNumber, model);
  }
);

export const createSolvingServiceReport = createAsyncThunk(
  "public/createSolvingServiceReport",
  async ({ serviceReportNumber, model }: any) => {
    return publicServiceReportsApi.createSolvingServiceReport(serviceReportNumber, model);
  }
);

export const deleteServiceReport = createAsyncThunk(
  "public/deleteServiceReport",
  async (serviceReportNumber: string) => {
    return publicServiceReportsApi.deleteServiceReport(serviceReportNumber);
  }
);

export const getCategories = createAsyncThunk(
  "public/getCategories",
  async () => {
    return customerCategoriesApi.getCategories();
  }
);

export const getMachineParts = createAsyncThunk(
  "public/getMachineParts",
  async () => {
    return publicServiceReportsApi.getMachineParts();
  }
);

export const getDocuments = createAsyncThunk(
  "public/getDocuments",
  async (machineId: string) => {
    return publicDocumentsApi.getDocuments(machineId);
  }
);

export interface PublicState {
  machine?: Machine;
  customer?: Customer;
  serviceReportsQuery: ServiceReportsQuery;
  serviceReports?: ServiceReport[];
  serviceReport?: ServiceReport;
  categories?: CategoryReference[];
  machineParts: MachinePartGroup[];
  documents?: Document[];
  loading: boolean;
  error: any;
}

const initialState: PublicState = {
  machine: undefined,
  customer: undefined,
  serviceReportsQuery: {
    statusCodes: [
      ServiceReportStatusCode.inProgress,
      ServiceReportStatusCode.solved
    ]
  },
  serviceReports: undefined,
  serviceReport: undefined,
  categories: undefined,
  machineParts: [],
  documents: undefined,
  loading: false,
  error: undefined
};

const publicSlice = createSlice({
  name: "public",
  initialState: initialState,
  reducers: {
    selectServiceReport: (state, action) => {
      state.serviceReport = action.payload;
    },
    setLoading: (state, { payload }) => {
      state.loading = payload;
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(getMachine.pending, (state) => {
        state.loading = true;
      })
      .addCase(getMachine.fulfilled, (state, { payload }) => {
        state.machine = payload;
        state.loading = false;
      })
      .addCase(getMachine.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getCustomer.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCustomer.fulfilled, (state, { payload }) => {
        state.customer = payload;
        state.loading = false;
      })
      .addCase(getCustomer.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getServiceReports.pending, (state) => {
        state.loading = true;
      })
      .addCase(getServiceReports.fulfilled, (state, { payload }) => {
        state.serviceReports = payload;
        state.loading = false;
      })
      .addCase(getServiceReports.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getServiceReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(getServiceReport.fulfilled, (state, { payload }) => {
        state.serviceReport = payload;
        state.loading = false;
      })
      .addCase(getServiceReport.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(createServiceReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(createServiceReport.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(createServiceReport.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(changeServiceReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(changeServiceReport.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(changeServiceReport.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(createSolvingServiceReport.pending, (state) => {
        state.loading = true;
      })
      .addCase(createSolvingServiceReport.fulfilled, (state, { payload }) => {
        state.loading = false;
      })
      .addCase(createSolvingServiceReport.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getCategories.pending, (state) => {
        state.loading = true;
      })
      .addCase(getCategories.fulfilled, (state, { payload }) => {
        state.categories = payload;
        state.loading = false;
      })
      .addCase(getCategories.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });

    builder
      .addCase(getMachineParts.pending, (state) => {
        // Don't set loading = true to avoid blocking UI
      })
      .addCase(getMachineParts.fulfilled, (state, { payload }) => {
        state.machineParts = payload;
      })
      .addCase(getMachineParts.rejected, (state, { error }) => {
        state.error = error;
      });

    builder
      .addCase(getDocuments.pending, (state) => {
        state.loading = true;
      })
      .addCase(getDocuments.fulfilled, (state, { payload }) => {
        state.documents = payload;
        state.loading = false;
      })
      .addCase(getDocuments.rejected, (state, { error }) => {
        state.error = error;
        state.loading = false;
      });
  }
});

export const {
  selectServiceReport,
  setLoading
} = publicSlice.actions;

export default publicSlice.reducer;